// ============================================================
// BaseHlsEngine — shared logic for HLS and LL-HLS engines
// ============================================================
import Hls, { type HlsConfig } from 'hls.js';
import type { StreamSource, QualityLevel } from '../../core/types';
import {
  createAdaptiveLatencyTarget,
  type AdaptiveLatencyTarget,
  type CorrectionState,
} from './latencyController';
import type { EngineContext, IEngine } from '../IEngine';
import {
  SPEED_REPORT_INTERVAL,
} from './constants';
import { createPlaylistLoaderClass } from './PlaylistLoader';
import {
  extractResponseCode as _extractResponseCode,
  isAccessDeniedError as _isAccessDeniedError,
  isPlayAbortError as _isPlayAbortError,
  getErrorMessage as _getErrorMessage,
} from './errorUtils';
import {
  getAverageSpeed as calcAverageSpeed,
  getOverallSpeed as calcOverallSpeed,
  type SpeedReportPayload,
  type SpeedReportSnapshot,
  startSpeedMonitorTimer,
  stopSpeedMonitorTimer,
} from './speedMonitor';
import { bindHlsEventsModule, type BaseHlsEngineBindings } from './hlsEvents';
import { bindVideoEvents as bindVideoEventsModule } from './videoEvents';

/** Hard cap on how long the startup spinner hides LL-HLS latency alignment. */
const INITIAL_LATENCY_ALIGNMENT_TIMEOUT_MS = 1_500;
/**
 * Buffer (seconds) that must sit ahead of the startup seek position before the
 * spinner is dropped. The seek consumes the buffered window, so revealing right
 * away would show a picture that stalls a few hundred ms later.
 */
const INITIAL_LATENCY_REVEAL_BUFFER_SECONDS = 0.6;
/** Poll interval of the playback stall watchdog. */
const STALL_WATCH_TICK_MS = 250;
/** Consecutive ticks without playhead progress before the watchdog acts. */
const STALL_WATCH_STALL_TICKS = 2;
/** Largest hole the watchdog will jump over. */
const STALL_WATCH_MAX_JUMP_SECONDS = 3;
/** Landing offset inside the next buffered range. */
const STALL_WATCH_JUMP_OFFSET_SECONDS = 0.05;
/** Nudge applied when the playhead is parked with data already under it. */
const STALL_WATCH_NUDGE_SECONDS = 0.1;
/** Buffer under the playhead that makes a parked position a decoder stall. */
const STALL_WATCH_NUDGE_MIN_BUFFER_SECONDS = 0.5;
/** Nudges attempted before recovering the media pipeline. */
const STALL_WATCH_MAX_NUDGES = 3;
/** Standard live HLS gets time to fetch a full segment before intervention. */
const STALL_WATCH_HLS_STALL_AFTER_MS = 10_000;
/** A live seek that never completes must not disable recovery indefinitely. */
const STALL_WATCH_SEEK_TIMEOUT_MS = 15_000;
/**
 * How long playback must be starved — playhead parked with no buffer under it
 * and no new media arriving — before the fetch pipeline is restarted. hls.js
 * keeps polling the playlist in this state without ever requesting a fragment,
 * so nothing recovers on its own.
 */
const STALL_WATCH_RELOAD_AFTER_MS = 3_000;
/** Minimum spacing between recovery attempts, so a dead stream is not hammered. */
const STALL_WATCH_RELOAD_COOLDOWN_MS = 10_000;
/**
 * Recovery escalates with every attempt that changes nothing: restarting the
 * fetch pipeline fixes an idle loader, but not a MediaSource that stopped
 * accepting appends — there fragments keep loading while the buffer never
 * grows, and only re-attaching the media (or reloading the stream) helps.
 */
const STALL_WATCH_RELOAD_ATTEMPTS_BEFORE_MEDIA_RECOVERY = 1;
const STALL_WATCH_RELOAD_ATTEMPTS_BEFORE_RESTART = 2;
/** Resume attempts for a media element paused while playback was requested. */
const STALL_WATCH_MAX_RESUMES = 3;
/** Buffer growth below this is treated as "the buffer is not filling". */
const STALL_WATCH_BUFFER_GROWTH_EPSILON = 0.01;

export abstract class BaseHlsEngine implements IEngine {
  abstract readonly name: string;
  abstract readonly isLowLatency: boolean;

  protected hls: Hls | null = null;
  protected video: HTMLVideoElement | null = null;
  protected container: HTMLElement | null = null;
  protected queryParams = '';
  private lastSource: StreamSource | null = null;
  private restartTimeout: ReturnType<typeof setTimeout> | null = null;
  private restartCount = 0;
  private mediaErrorCount = 0;

  // Speed monitor state — accumulates across HLS restarts
  private speedDownloadSpeeds: number[] = [];
  private speedTotalBytes = 0;
  private speedTotalTime = 0;
  private speedReportTimer: ReturnType<typeof setInterval> | null = null;

  // Frag sample throttle
  private lastFragSampleAt = 0;

  // Fragment loading gate — true once hls.startLoad() has been called for the current HLS instance
  private hlsLoadStarted = false;
  // Set before a scheduled restart so MANIFEST_PARSED knows to resume playback
  private shouldResumeOnNextManifest = false;
  // True once play() has been explicitly called; cleared by stop()/pause()/setupHls().
  // Guards scheduleRestart: only resume after a restart when the user actually wanted playback,
  // not when store.loading was set by the preview-preload phase (autoplay=false).
  private _playRequested = false;
  // True while hls.js is executing a quality-level switch (SourceBuffer may be flushed)
  private isLevelSwitching = false;
  // Timestamp when the most recent level switch started — used for switch-duration logging
  private levelSwitchStartedAt = 0;
  // Consecutive non-fatal fragLoadErrors — reset on FRAG_BUFFERED or engine restart
  private fragLoadErrorCount = 0;
  // Guard against re-asserting hls.currentLevel on every LEVEL_SWITCHED mismatch,
  // which would cancel fragments repeatedly and cause 404 storms on LL-HLS.
  // Cleared when hls.js confirms it switched to the user-requested level.
  private pendingQualityReassert = false;
  // Timestamp (Date.now) of the latest manual-quality re-assert attempt.
  // Used to give hls.js a short grace period before falling back to ABR auto.
  private lastQualityReassertAt = 0;
  // Live quality switch probe — retries a HEAD/GET of the target level's playlist URL
  // until the CDN returns 200, then applies the switch. Cleared on superseding request or stop.
  private liveQualitySwitchTimer: ReturnType<typeof setTimeout> | null = null;
  private liveQualitySwitchIdx: number | null = null;

  // Consecutive fragGap errors — reset on FRAG_BUFFERED
  private fragGapErrorCount = 0;
  // When true, latency playback-rate adjustments are suspended until the buffer
  // recovers above bufferRecoveryThreshold(latencyTarget.get())
  // Readable by subclasses: the LL ABR must not add load while the engine is
  // rebuilding buffer headroom after a stall.
  protected isLatencyPausedForBuffer = false;
  /**
   * Latency the controller steers towards. It rises after each stall so the
   * player keeps a buffer margin instead of racing back to the minimum and
   * stalling again on the next late part, and decays back once playback holds.
   */
  readonly latencyTarget: AdaptiveLatencyTarget = createAdaptiveLatencyTarget({
    debug: (...args: unknown[]) => this.logger.debug(...args),
  });
  // Throttle: timestamp of the last applyLatencyControl call (500ms interval)
  private lastLatencyRateAt = 0;
  // Correction state for threshold-gated latency control — persisted between calls
  private latencyCorrectionState: CorrectionState = 'none';
  // One-shot first-frame alignment for LL-HLS. Reset for every Hls instance.
  private initialLiveLatencyAligned = false;
  // The one-shot startup seek has run; the reveal now waits for the buffer to
  // refill so the spinner covers the whole jump instead of flickering after it.
  private initialLiveLatencySeeked = false;
  private initialLiveLatencyTimer: ReturnType<typeof setTimeout> | null = null;
  // Playback stall watchdog — LL-HLS part appends can leave the playhead inside
  // a buffer hole, sitting on undecoded data, or starved with the fetch pipeline
  // idle. In all three the picture freezes while latency keeps climbing.
  private stallWatchTimer: ReturnType<typeof setInterval> | null = null;
  private stallWatchLastTime = -1;
  private stallWatchLastAt = 0;
  private stallWatchStalledTicks = 0;
  private stallWatchNudges = 0;
  private stallWatchStalledSince = 0;
  private stallWatchSeekingSince = 0;
  private stallWatchBufferEnd = -1;
  private stallWatchBufferGrewAt = 0;
  private stallWatchReloadedAt = 0;
  private stallWatchRecoveryAttempts = 0;
  private stallWatchResumes = 0;
  // Logical mute state while startup seeks are hidden. The media element stays
  // muted until alignment completes, without changing the user's mute setting.
  private initialLiveLatencyMutedState: boolean | null = null;

  protected readonly eventBus;
  protected readonly store;
  protected readonly logger;
  private programmaticVolumeChange = 0;

  constructor(protected ctx: EngineContext) {
    this.eventBus = ctx.eventBus;
    this.store = ctx.store;
    this.logger = ctx.logger;
  }

  /** Subclasses provide engine-specific hls.js config overrides. */
  protected abstract getHlsConfig(): Partial<HlsConfig>;

  protected applyQualityLevel(idx: number): void {
    const hls = this.hls!;
    hls.nextLevel = idx;
  }

  // --- IEngine implementation ---

  attach(container: HTMLElement): void {
    this.container = container;
    this.video = document.createElement('video');
    this.video.setAttribute('playsinline', 'playsinline');
    this.video.setAttribute('webkit-playsinline', 'webkit-playsinline');
    this.video.style.width = '100%';
    this.video.style.height = '100%';
    this.video.preservesPitch = true;
    container.appendChild(this.video);
    this.bindVideoEvents();
  }

  load(source: StreamSource, queryParams: string): void {
    this.queryParams = queryParams;
    this.lastSource = source;
    this.mediaErrorCount = 0;
    this.restartCount = 0;
    const url = source.master || source.source || '';
    if (!url) {
      this.logger.error('No stream URL provided');
      return;
    }

    this.setupHls();
    this.hls!.loadSource(url);
    this.hls!.attachMedia(this.video!);
  }

  /** Update future playlist authentication without resetting the media pipeline. */
  updateSourceAuth(source: StreamSource, queryParams: string): void {
    this.lastSource = source;
    this.queryParams = queryParams;
  }

  play(): void {
    if (!this.video) return;
    if (this.ctx.isBackgroundPlayback?.() !== true) this.eventBus.emit('playback:requested');
    this._playRequested = true;
    if (this.isLowLatency && !this.initialLiveLatencyAligned) {
      this.muteInitialLiveLatencyAudio();
      this.setStartupHiding(true);
    }
    this.startStallWatchdog();
    // Start loading fragments if not yet started (e.g. autoplay is off and user pressed play)
    if (this.hls && !this.hlsLoadStarted) {
      // Resume-from-pause on live must jump to the live edge: hls.js overrides
      // startLoad(-1) with its internal lastCurrentTime when a media position
      // already exists, which would restart loading at the stale pause point
      // (and 404-loop once the pause point leaves the DVR window). Passing an
      // explicit positive position bypasses that override.
      // VOD keeps the paused position — no seek, plain startLoad(-1).
      const resumePos = this.getLiveResumePosition();
      if (resumePos !== null) {
        this.logger.debug(
          `play(): live resume — jumping from ${this.video.currentTime.toFixed(2)}s to live edge ${resumePos.toFixed(2)}s`,
        );
        this.video.currentTime = resumePos;
        this.hls.startLoad(resumePos);
      } else {
        this.hls.startLoad(-1);
      }
      this.hlsLoadStarted = true;
    }
    this.video.play().catch((e: unknown) => {
      if (this.isPlayAbortError(e)) {
        return;
      }
      // Autoplay blocked — try muted
      this.logger.warn('Autoplay blocked, trying muted:', this.getErrorMessage(e));
      if (!this.video) return;
      this.programmaticVolumeChange += 1;
      this.video.muted = true;
      this.store.setState({ isMuted: true });
      return this.video.play().catch((retryError: unknown) => {
        if (this.isPlayAbortError(retryError)) {
          return;
        }
        this.logger.warn('Muted autoplay failed:', this.getErrorMessage(retryError));
        this.eventBus.emit('playback:blocked');
      });
    });
  }

  pause(): void {
    this.eventBus.emit('playback:cancelled');
    this._playRequested = false;
    this.clearStallWatchdog();
    this.clearInitialLiveLatencyTimer();
    this.restoreInitialLiveLatencyAudio();
    this.setStartupHiding(false);
    this.store.setState({ loading: false });
    // Stop fetching media segments immediately — resume from play()
    if (this.hls) {
      this.hls.stopLoad();
      this.hlsLoadStarted = false;
    }
    this.video?.pause();
  }

  stop(): void {
    this._playRequested = false;
    this.clearStallWatchdog();
    this.clearRestartTimeout();
    this.clearLiveQualitySwitch();
    this.clearInitialLiveLatencyTimer();
    this.restoreInitialLiveLatencyAudio();
    if (this.hls) {
      this.hls.stopLoad();
    }
    if (this.video) {
      this.video.pause();
      this.video.removeAttribute('src');
      this.video.load();
    }
    this.setStartupHiding(false);
    this.store.setState({ isPlaying: false, loading: false });
    this.eventBus.emit('stopped');
  }

  destroy(): void {
    this.stopSpeedMonitor();
    this.clearRestartTimeout();
    this.stop();
    if (this.hls) {
      this.hls.destroy();
      this.hls = null;
    }
    if (this.video) {
      this.video.remove();
      this.video = null;
    }
    this.eventBus.emit('engine:destroyed');
  }

  setQuality(level: QualityLevel): void {
    if (!this.hls) return;

    this.logger.debug('[HLS]', `setQuality: requested "${level}" (${this.hls.levels.length} levels in manifest)`);

    // Clear any pending live probe — new request supersedes previous switch cycle.
    this.clearLiveQualitySwitch();

    if (level === 'auto') {
      this.pendingQualityReassert = false;
      this.lastQualityReassertAt = 0;
      this.hls.currentLevel = -1;
      this.store.setState({ quality: 'auto' });
      this.eventBus.emit('quality:changed', 'auto');
      this.logger.debug('[HLS]', 'setQuality: ABR enabled (auto)');
      return;
    }

    const height = parseInt(level, 10);
    if (isNaN(height)) {
      this.logger.warn(`setQuality: unrecognised level format "${level}" — ignored`);
      return;
    }

    const idx = this.hls.levels.findIndex(l => l.height === height);

    if (idx === -1) {
      this.pendingQualityReassert = false;
      this.lastQualityReassertAt = 0;
      this.hls.currentLevel = -1;
      this.store.setState({ quality: 'auto' });
      this.eventBus.emit('quality:changed', 'auto');
      this.logger.warn(`setQuality: "${level}" not in manifest — falling back to auto`);
      return;
    }
    this.logger.debug('[HLS]', `setQuality: "${level}" matched at index ${idx}`);

    // Clear any pending re-assert — new request supersedes previous switch cycle.
    this.pendingQualityReassert = false;
    this.lastQualityReassertAt = 0;
    const lockedLevel = this.hls.levels[idx];

    // For live streams: probe the target level's own playlist URL once before switching.
    // hls.js segments for inactive renditions may not yet be assembled on the CDN — a
    // 404 here means "not ready"; we retry every 2 s until the CDN confirms 200.
    // For VOD: all segments exist, apply immediately.
    const currentDetails = this.hls.currentLevel >= 0 ? this.hls.levels[this.hls.currentLevel]?.details : undefined;
    const isLive = currentDetails?.live ?? false;
    if (isLive) {
      void this.probeAndApplyQualityLevel(idx);
      this.logger.warn(
        `setQuality: live stream — probing target level ${idx} (${lockedLevel?.height}p) before switch`,
      );
    } else {
      // VOD or engine cold start (no playlist details yet) — apply immediately.
      this.applyQualityLevel(idx);
      this.logger.warn(
        `setQuality: locked to index ${idx} — ${lockedLevel?.height}p @ ${lockedLevel?.bitrate} bps (ABR disabled)`,
      );
    }
    this.store.setState({ quality: level });
  }

  getQualities(): QualityLevel[] {
    if (!this.hls || this.hls.levels.length <= 1) return [];
    const levels: QualityLevel[] = ['auto'];
    for (const l of this.hls.levels) {
      const label = String(l.height) as QualityLevel;
      if (!levels.includes(label)) levels.push(label);
    }
    return levels;
  }

  setVolume(value: number): void {
    const rounded = Math.round(value * 100) / 100;
    const requestedMuted = rounded === 0;
    if (this.initialLiveLatencyMutedState !== null) {
      this.initialLiveLatencyMutedState = requestedMuted;
    }
    if (this.video) {
      this.programmaticVolumeChange += 2; // volume + muted assignments
      this.video.volume = rounded;
      this.video.muted = this.initialLiveLatencyMutedState !== null || requestedMuted;
    }
    this.store.setState({ volume: rounded, isMuted: requestedMuted });
  }

  seek(offset: number): void {
    if (this.video) {
      this.video.currentTime = Math.max(0, this.video.currentTime + offset);
    }
  }

  getLatency(): number {
    return this.hls?.latency ?? 0;
  }

  getBufferLength(): number {
    if (!this.video || !this.video.buffered.length) return 0;
    for (let i = 0; i < this.video.buffered.length; i++) {
      if (
        this.video.currentTime >= this.video.buffered.start(i) &&
        this.video.currentTime <= this.video.buffered.end(i)
      ) {
        return this.video.buffered.end(i) - this.video.currentTime;
      }
    }
    return 0;
  }

  getBandwidth(): number {
    return this.hls ? this.hls.bandwidthEstimate / 1_000_000 : 0;
  }

  getVideoElement(): HTMLVideoElement | null {
    return this.video;
  }

  clean(): void {
    // hls.js handles back buffer automatically via backBufferLength config
    this.logger.debug('[HLS]', 'clean() called');
  }

  /**
   * Called when FRAG_LOAD_ERROR_THRESHOLD consecutive non-fatal fragLoadErrors
   * have occurred. Subclasses override to perform engine-specific recovery
   * (e.g. LLHlsEngine jumps to the live edge).
   */
  protected onFragLoadErrorThreshold(): void {
    // Default recovery: reset to ABR auto so hls.js can select a rendition
    // that is actually available on the CDN at the current playhead position.
    this.logger.warn('fragLoadError threshold — falling back to ABR auto');
  }

  /**
   * Called when a non-fatal fragLoadError occurs while a level switch is in
   * progress. These errors are NOT counted toward FRAG_LOAD_ERROR_THRESHOLD.
   * Subclasses override to implement engine-specific retry logic (e.g. step
   * back to an earlier CDN-assembled segment and restart loading from there).
   */
  protected onFragLoadErrorDuringSwitch(_fragStart: number): void {
    // Default: no-op — hls.js will retry the fragment automatically
  }

  /**
   * Live-edge position to resume at after pause, or null for VOD / unknown
   * (caller falls back to hls.startLoad(-1)). Uses hls.liveSyncPosition —
   * hls.js's own deterministic live-edge calculation.
   */
  protected getLiveResumePosition(): number | null {
    if (!this.hls) return null;

    // isVod is the single source of truth — derived by Store from
    // streamType, which is set from the same manifest data via LEVEL_LOADED.
    if (this.store.getState().isVod) return null;
    if (this.isLivePlaylistStale()) return null;

    const syncPos = this.hls.liveSyncPosition;
    if (typeof syncPos === 'number' && isFinite(syncPos) && syncPos > 0) {
      return syncPos;
    }

    return null;
  }

  private isLivePlaylistStale(): boolean {
    const details = this.hls?.latestLevelDetails;
    if (!details?.live || !Number.isFinite(details.age)) return false;
    // Allow slow updates with variable GOP, but never seek into a timeline
    // that has not been refreshed for multiple target durations.
    return details.age > Math.max(10, details.targetduration * 3);
  }

  // --- Private ---

  private setupHls(): void {
    this.store.setState({ cdnServer: null, cdnServerIp: null, cdnServerSource: null });
    if (this.hls) {
      this.hls.destroy();
    }

    this.hlsLoadStarted = false;
    this._playRequested = false;
    this.fragLoadErrorCount = 0;
    this.fragGapErrorCount = 0;
    this.isLatencyPausedForBuffer = false;
    this.latencyTarget.reset();
    this.pendingQualityReassert = false;
    this.lastQualityReassertAt = 0;
    this.latencyCorrectionState = 'none';
    this.restoreInitialLiveLatencyAudio();
    this.setStartupHiding(false);
    this.initialLiveLatencyAligned = false;
    this.initialLiveLatencySeeked = false;
    this.clearInitialLiveLatencyTimer();
    this.clearLiveQualitySwitch();

    const baseConfig = this.getHlsConfig();

    const config: Partial<HlsConfig> = {
      ...baseConfig,
      pLoader: createPlaylistLoaderClass(() => this.queryParams, this.isLowLatency) as any,
    };

    this.hls = new Hls(config);
    this.bindHlsEvents();
  }

  private bindHlsEvents(): void {
    bindHlsEventsModule(this as unknown as BaseHlsEngineBindings);
  }

  private clearLiveQualitySwitch(): void {
    if (this.liveQualitySwitchTimer !== null) {
      clearTimeout(this.liveQualitySwitchTimer);
      this.liveQualitySwitchTimer = null;
    }
    this.liveQualitySwitchIdx = null;
  }

  /**
   * Probes the target level's playlist URL and applies the quality switch as soon
   * as the CDN confirms the rendition is ready (HTTP 200). Retries every 2 s on
   * 404 or network error (unassembled chunk / CDN propagation lag).
   */
  private async probeAndApplyQualityLevel(idx: number): Promise<void> {
    this.logger.warn(`[level:probe] probeAndApplyQualityLevel called idx=${idx}`);
    this.liveQualitySwitchIdx = idx;

    const rawUrl = this.hls?.levels[idx]?.url[0];
    if (!rawUrl) {
      // No URL to probe — apply immediately
      this.logger.warn(`[level:probe] idx=${idx} no URL — applying immediately`);
      if (this.liveQualitySwitchIdx === idx) {
        this.liveQualitySwitchIdx = null;
        this.applyQualityLevel(idx);
      }
      return;
    }

    const fullUrl = rawUrl + (this.queryParams ? (rawUrl.includes('?') ? '&' : '?') + this.queryParams : '');
    this.logger.warn(`[level:probe] idx=${idx} probing url=${fullUrl}`);

    const tryProbe = async (): Promise<void> => {
      if (this.liveQualitySwitchIdx !== idx) {
        this.logger.warn(`[level:probe] idx=${idx} superseded — aborting`);
        return;
      }
      try {
        const res = await fetch(fullUrl);
        if (res.ok) {
          if (this.liveQualitySwitchIdx === idx) {
            this.logger.warn(`[level:probe] idx=${idx} ready (${res.status}) — applying switch`);
            this.logger.info(`setQuality: level ${idx} ready (${res.status}) — applying switch`);
            this.liveQualitySwitchIdx = null;
            this.applyQualityLevel(idx);
          }
          return;
        }
        this.logger.debug(`[level:probe] idx=${idx} not ready (${res.status}) — retrying in 2s`);
      } catch (err) {
        this.logger.debug(`[level:probe] idx=${idx} fetch error: ${this.getErrorMessage(err)} — retrying in 2s`);
      }
      if (this.liveQualitySwitchIdx === idx) {
        this.liveQualitySwitchTimer = setTimeout(() => { void tryProbe(); }, 2000);
      }
    };

    void tryProbe();
  }

  private bindVideoEvents(): void {
    bindVideoEventsModule({
      video: this.video!,
      eventBus: this.eventBus,
      store: this.store,
      getSource: () => this.lastSource?.master ?? this.lastSource?.source,
      getProgrammaticVolumeChange: () => this.programmaticVolumeChange,
      setProgrammaticVolumeChange: value => {
        this.programmaticVolumeChange = value;
      },
      getMutedStateOverride: () => this.initialLiveLatencyMutedState,
      suppressLoadingState: () =>
        this.ctx.suppressLoadingState?.() ?? this.ctx.isBackgroundPlayback?.() === true,
      onPlaying: () => {
        this.restartCount = 0;
        // Playback can start without a play() call of ours — an engine handoff
        // resumes the new video directly — so arm the watchdog here as well.
        this.startStallWatchdog();
        if (this.isLowLatency && !this.initialLiveLatencyAligned) {
          this.muteInitialLiveLatencyAudio();
          this.setStartupHiding(true);
          if (this.initialLiveLatencyTimer === null) {
            // Safety net only: alignment normally completes on the first
            // playlist update after playback starts. Keep it short so a stream
            // that never reports a usable estimate still starts quickly.
            this.initialLiveLatencyTimer = setTimeout(() => {
              this.logger.warn('[latency:start] alignment timeout — revealing playback');
              this.completeInitialLiveLatencyAlignment();
            }, INITIAL_LATENCY_ALIGNMENT_TIMEOUT_MS);
          }
          return true;
        }
        return false;
      },
    });
  }

  /**
   * Marks the window in which the loading overlay may blank the picture. Only
   * the one-shot LL-HLS startup seek is hidden this way: mid-playback stalls
   * must keep the last frame on screen behind a transparent spinner, otherwise
   * every short lag reads as the picture disappearing. The hidden video of an
   * engine handoff is off-screen already, so it never sets the flag.
   */
  private setStartupHiding(hiding: boolean): void {
    const active = hiding && this.ctx.isBackgroundPlayback?.() !== true;
    if (this.store.getState().startupHiding === active) return;
    this.store.setState({ startupHiding: active });
  }

  /** True once playback can continue past the startup seek without stalling. */
  private hasInitialLiveLatencyRevealBuffer(): boolean {
    if (!this.video) return false;
    return (
      this.video.readyState >= 3 &&
      this.getBufferLength() >= INITIAL_LATENCY_REVEAL_BUFFER_SECONDS
    );
  }

  private completeInitialLiveLatencyAlignment(): void {
    this.initialLiveLatencyAligned = true;
    this.setStartupHiding(false);
    this.initialLiveLatencySeeked = false;
    this.clearInitialLiveLatencyTimer();
    // During an engine handoff this lets GGPlayer reveal the already-aligned
    // video and retire the old engine before the new audio is restored.
    this.ctx.onInitialLiveLatencyAligned?.();
    if (this.ctx.isBackgroundPlayback?.()) {
      // The handoff may deliberately wait for a short stable-playback window.
      // Keep the hidden video muted; GGPlayer restores its volume at reveal.
      this.initialLiveLatencyMutedState = null;
    } else {
      this.restoreInitialLiveLatencyAudio();
    }
    if (this.store.getState().isPlaying && !this.ctx.isBackgroundPlayback?.()) {
      this.store.setState({ loading: false });
    }
  }

  /**
   * Watches playback for a playhead that stops advancing and recovers it before
   * the viewer sees a dead picture. Three shapes show up on live HLS:
   *
   *  1. the playhead sits inside a hole between buffered ranges,
   *  2. it sits on buffered data the decoder never starts,
   *  3. it is starved — nothing under it and nothing arriving — while hls.js
   *     keeps polling the playlist without requesting a single fragment.
   *
   * hls.js only reacts to (1) and (2) once its own stall detector fires, and
   * not at all to (3), where the stream stays frozen with latency climbing.
   */
  private startStallWatchdog(): void {
    if (this.stallWatchTimer !== null) return;
    this.resetStallWatchProgress();
    this.stallWatchSeekingSince = 0;
    this.stallWatchReloadedAt = 0;
    this.stallWatchRecoveryAttempts = 0;
    this.stallWatchTimer = setInterval(() => {
      this.checkPlaybackStall();
    }, STALL_WATCH_TICK_MS);
  }

  private clearStallWatchdog(): void {
    if (this.stallWatchTimer !== null) {
      clearInterval(this.stallWatchTimer);
      this.stallWatchTimer = null;
    }
    this.resetStallWatchProgress();
    this.stallWatchSeekingSince = 0;
    this.stallWatchNudges = 0;
    this.stallWatchResumes = 0;
  }

  private resetStallWatchProgress(): void {
    this.stallWatchLastTime = -1;
    this.stallWatchLastAt = Date.now();
    this.stallWatchStalledTicks = 0;
    this.stallWatchStalledSince = 0;
    this.stallWatchBufferEnd = -1;
    this.stallWatchBufferGrewAt = Date.now();
  }

  /** Furthest buffered position, used to tell "not filling" from "filling slowly". */
  private getBufferedEnd(): number {
    const video = this.video;
    if (!video) return 0;
    let end = 0;
    for (let i = 0; i < video.buffered.length; i++) {
      end = Math.max(end, video.buffered.end(i));
    }
    return end;
  }

  private checkPlaybackStall(): void {
    const video = this.video;
    if (!video) {
      this.clearStallWatchdog();
      return;
    }
    const state = this.store.getState();
    // Standard HLS is monitored only after the playlist confirms live. Keep
    // the existing LL-HLS path, but never resume or nudge a known offline stream.
    if (!state.isOnline || (!this.isLowLatency && state.streamType !== 'live')) {
      this.resetStallWatchProgress();
      this.stallWatchSeekingSince = 0;
      return;
    }
    if (video.seeking) {
      const now = Date.now();
      if (this.stallWatchSeekingSince === 0) {
        this.stallWatchSeekingSince = now;
        this.resetStallWatchProgress();
      }
      if (this._playRequested && this.ctx.isBackgroundPlayback?.() !== true && !state.isVod &&
        now - this.stallWatchSeekingSince >= STALL_WATCH_SEEK_TIMEOUT_MS) {
        this.stallWatchStalledSince = this.stallWatchSeekingSince;
        this.recoverStalledPipeline(now, video.currentTime, this.getBufferedEnd(), 'seek_stall');
      }
      return;
    }
    this.stallWatchSeekingSince = 0;
    if (video.paused) {
      this.resumePausedPlayback();
      // Re-attaching media can leave it paused. Failed automatic resumes must
      // not strand the recovery ladder after its media-recovery step.
      if (this._playRequested && this.ctx.isBackgroundPlayback?.() !== true &&
        !state.isVod && this.stallWatchResumes >= STALL_WATCH_MAX_RESUMES &&
        this.stallWatchRecoveryAttempts >= STALL_WATCH_RELOAD_ATTEMPTS_BEFORE_RESTART) {
        this.stallWatchStalledSince = this.stallWatchReloadedAt;
        this.recoverStalledPipeline(Date.now(), video.currentTime, this.getBufferedEnd(), 'decoder_stall');
      }
      return;
    }
    this.stallWatchResumes = 0;

    const now = Date.now();
    const bufferedEnd = this.getBufferedEnd();
    if (bufferedEnd > this.stallWatchBufferEnd + STALL_WATCH_BUFFER_GROWTH_EPSILON) {
      this.stallWatchBufferEnd = bufferedEnd;
      this.stallWatchBufferGrewAt = now;
    }

    const currentTime = video.currentTime;
    const elapsed = Math.max(0, now - this.stallWatchLastAt) / 1000;
    this.stallWatchLastAt = now;
    if (currentTime !== this.stallWatchLastTime) {
      const advanced = currentTime - this.stallWatchLastTime;
      // A seek/nudge moves currentTime without decoding. Keep escalation until
      // buffered playback advances at a plausible rate outside a seek.
      const progressed = this.stallWatchLastTime >= 0 && advanced > 0 &&
        advanced <= elapsed * Math.max(1, video.playbackRate) + 0.05 &&
        video.readyState >= 3 && this.getBufferLength() > 0;
      if (progressed) {
        this.stallWatchNudges = 0;
        this.stallWatchRecoveryAttempts = 0;
      }
      this.stallWatchLastTime = currentTime;
      this.stallWatchStalledTicks = 0;
      this.stallWatchStalledSince = 0;
      return;
    }
    this.stallWatchStalledTicks += 1;
    if (this.stallWatchStalledTicks < STALL_WATCH_STALL_TICKS) return;
    if (this.stallWatchStalledSince === 0) this.stallWatchStalledSince = now;
    if (!this.isLowLatency && this.stallWatchNudges === 0 &&
      now - this.stallWatchStalledSince < STALL_WATCH_HLS_STALL_AFTER_MS) return;

    for (let i = 0; i < video.buffered.length; i++) {
      const start = video.buffered.start(i);
      if (start <= currentTime) continue;
      const gap = start - currentTime;
      if (gap > STALL_WATCH_MAX_JUMP_SECONDS) break;
      this.logger.warn(
        `[stall:gap] playhead parked at ${currentTime.toFixed(2)}s —` +
          ` jumping ${gap.toFixed(2)}s into buffered range at ${start.toFixed(2)}s`,
      );
      video.currentTime = start + STALL_WATCH_JUMP_OFFSET_SECONDS;
      this.eventBus.emit('playback:recovery', { action: 'gap_jump', reason: 'buffer_hole' });
      this.stallWatchLastTime = -1;
      this.stallWatchStalledTicks = 0;
      this.stallWatchStalledSince = 0;
      return;
    }

    const bufferLength = this.getBufferLength();

    // Data under the playhead: the media element is sitting on it without
    // decoding. A short nudge (what hls.js does once its own stall detector
    // fires) restarts playback well before that detector kicks in.
    if (bufferLength >= STALL_WATCH_NUDGE_MIN_BUFFER_SECONDS) {
      if (this.stallWatchNudges < STALL_WATCH_MAX_NUDGES) {
        this.stallWatchNudges += 1;
        this.logger.warn(
          `[stall:nudge] playhead parked at ${currentTime.toFixed(2)}s with` +
            ` ${bufferLength.toFixed(2)}s buffered — nudging` +
            ` (${this.stallWatchNudges}/${STALL_WATCH_MAX_NUDGES})`,
        );
        video.currentTime = currentTime + STALL_WATCH_NUDGE_SECONDS;
        this.stallWatchLastTime = video.currentTime;
        this.eventBus.emit('playback:recovery', { action: 'nudge', reason: 'decoder_stall', attempt: this.stallWatchNudges });
        void video.play().catch(() => undefined);
        this.stallWatchStalledTicks = 0;
      } else if (!state.isVod) {
        // Buffered data can keep arriving while the decoder is stuck. Once
        // nudges are exhausted, escalate even if the buffer continues growing.
        this.recoverStalledPipeline(now, currentTime, bufferedEnd, 'decoder_stall');
      }
      return;
    }

    // Starved: no buffer under the playhead and no new media appended for a
    // while. Restarting the fetch pipeline is the only exit — hls.js will keep
    // refreshing the playlist forever without asking for a fragment.
    this.recoverStalledPipeline(now, currentTime, bufferedEnd);
  }

  /**
   * The media element can end up paused while the pipeline keeps loading — the
   * picture freezes on a frame with data still arriving, and nothing in hls.js
   * resumes it. Only touched when playback was actually requested: a user pause
   * clears `_playRequested`, and a warming handoff engine is meant to stay idle.
   */
  private resumePausedPlayback(): void {
    const video = this.video;
    if (!video) return;
    this.resetStallWatchProgress();
    if (!this._playRequested || this.ctx.isBackgroundPlayback?.() === true) {
      this.stallWatchResumes = 0;
      return;
    }
    if (this.stallWatchResumes >= STALL_WATCH_MAX_RESUMES) return;
    this.stallWatchResumes += 1;
    this.eventBus.emit('playback:recovery', { action: 'resume', reason: 'unexpected_pause', attempt: this.stallWatchResumes });
    this.logger.warn(
      `[stall:resume] media paused at ${video.currentTime.toFixed(2)}s while playback` +
        ` was requested — resuming (${this.stallWatchResumes}/${STALL_WATCH_MAX_RESUMES})`,
    );
    void video.play().catch(err => {
      this.logger.warn(`[stall:resume] failed: ${this.getErrorMessage(err)}`);
    });
  }

  /** MediaSource state, when hls.js exposes it — the usual cause of dead appends. */
  private getMediaSourceState(): string {
    try {
      const source = (this.hls as unknown as {
        bufferController?: { mediaSource?: { readyState?: string } };
      } | null)?.bufferController?.mediaSource;
      return source?.readyState ?? 'unknown';
    } catch {
      return 'unknown';
    }
  }

  /**
   * Escalating recovery for a stalled playhead. Each step is tried once per
   * cooldown, and only while nothing has changed: playback progress resets the
   * ladder, so a stream that recovers never sees the heavier steps.
   */
  private recoverStalledPipeline(
    now: number,
    currentTime: number,
    bufferedEnd: number,
    mediaStall: 'decoder_stall' | 'seek_stall' | null = null,
  ): void {
    const hls = this.hls;
    if (!hls || !this.hlsLoadStarted) return;
    if (this.store.getState().isOnline === false) return;
    const waitMs = mediaStall || this.isLowLatency
      ? STALL_WATCH_RELOAD_AFTER_MS : STALL_WATCH_HLS_STALL_AFTER_MS;
    if (now - this.stallWatchStalledSince < waitMs) return;
    if (!mediaStall && now - this.stallWatchBufferGrewAt < waitMs) return;
    if (now - this.stallWatchReloadedAt < STALL_WATCH_RELOAD_COOLDOWN_MS) return;

    this.stallWatchReloadedAt = now;
    if (mediaStall) {
      // Buffered decoder stalls and stuck seeks need the media pipeline reset.
      this.stallWatchRecoveryAttempts = Math.max(
        this.stallWatchRecoveryAttempts, STALL_WATCH_RELOAD_ATTEMPTS_BEFORE_MEDIA_RECOVERY,
      );
    }
    this.stallWatchRecoveryAttempts += 1;
    const attempt = this.stallWatchRecoveryAttempts;
    const reason = mediaStall ?? 'starved_buffer';
    const state =
      `${reason} at ${currentTime.toFixed(2)}s for` +
      ` ${((now - this.stallWatchStalledSince) / 1000).toFixed(1)}s without playback progress` +
      ` (buffered to ${bufferedEnd.toFixed(2)}s, level=${hls.loadLevel},` +
      ` next=${hls.nextLevel}, auto=${String(hls.autoLevelEnabled)},` +
      ` mediaSource=${this.getMediaSourceState()},` +
      ` mediaError=${this.video?.error?.code ?? 'none'})`;

    this.eventBus.emit('buffer:stall', {
      currentTime,
      bufferLength: this.getBufferLength(),
      src: this.lastSource?.master ?? this.lastSource?.source,
      quality: this.store.getState().quality,
    });

    if (this.isLivePlaylistStale()) {
      this.logger.warn('[stall:restart] live playlist is stale — reloading the master');
      this.scheduleRestart(0);
      return;
    }

    if (attempt <= STALL_WATCH_RELOAD_ATTEMPTS_BEFORE_MEDIA_RECOVERY) {
      this.eventBus.emit('playback:recovery', { action: 'reload_pipeline', reason: 'starved_buffer', attempt });
      this.logger.warn(`[stall:reload] ${state} — restarting the fetch pipeline`);
      hls.stopLoad();
      // A starved live playhead is typically parked outside the DVR window:
      // startLoad(-1) would be overridden by hls.js with that stale
      // lastCurrentTime and re-request unavailable fragments (404-loop).
      // Resume at the live edge instead — same pattern as play() and
      // onFragLoadErrorThreshold. VOD/unknown falls back to startLoad(-1).
      const resumePos = this.getLiveResumePosition();
      if (resumePos !== null) {
        this.logger.warn(
          `[stall:reload] live resume — jumping from ${currentTime.toFixed(2)}s to live edge ${resumePos.toFixed(2)}s`,
        );
        this.video!.currentTime = resumePos;
        this.stallWatchLastTime = this.video!.currentTime;
        hls.startLoad(resumePos);
      } else {
        hls.startLoad(-1);
      }
      return;
    }

    if (attempt <= STALL_WATCH_RELOAD_ATTEMPTS_BEFORE_RESTART) {
      // Either appends or decoding stopped. Re-attaching the media repairs
      // the pipeline after loader recovery or the nudge budget was exhausted.
      this.logger.warn(`[stall:recover] ${state} — recovering the media element`);
      try {
        this.eventBus.emit('playback:recovery', { action: 'recover_media', reason, attempt });
        hls.recoverMediaError();
        return;
      } catch (err) {
        this.logger.warn(`[stall:recover] failed: ${this.getErrorMessage(err)}`);
      }
    }

    this.logger.warn(`[stall:restart] ${state} — reloading the stream`);
    this.stallWatchRecoveryAttempts = 0;
    this.scheduleRestart(0);
  }

  private clearInitialLiveLatencyTimer(): void {
    if (this.initialLiveLatencyTimer !== null) {
      clearTimeout(this.initialLiveLatencyTimer);
      this.initialLiveLatencyTimer = null;
    }
  }

  private muteInitialLiveLatencyAudio(): void {
    if (!this.video || this.initialLiveLatencyMutedState !== null) return;
    this.initialLiveLatencyMutedState = this.store.getState().isMuted;
    if (!this.video.muted) {
      this.programmaticVolumeChange += 1;
      this.video.muted = true;
    }
  }

  private restoreInitialLiveLatencyAudio(): void {
    if (!this.video || this.initialLiveLatencyMutedState === null) return;
    const muted = this.initialLiveLatencyMutedState;
    this.initialLiveLatencyMutedState = null;
    if (this.video.muted !== muted) {
      this.programmaticVolumeChange += 1;
      this.video.muted = muted;
    }
    this.store.setState({ isMuted: muted });
  }

  private scheduleRestart(delay: number): void {
    this.clearRestartTimeout();
    // Remember whether we were actively playing when the error occurred so that
    // MANIFEST_PARSED can resume playback automatically after the restart.
    // Use _playRequested to distinguish deliberate playback from the silent
    // preview-preload phase (autoplay=false): store.loading can be true in both
    // cases, but _playRequested is only set when play() was explicitly called.
    this.shouldResumeOnNextManifest = this.store.getState().isPlaying ||
      (this.store.getState().loading && this._playRequested);
    this.restartTimeout = setTimeout(() => {
      if (!this.lastSource || !this.container) return;
      this.eventBus.emit('playback:recovery', { action: 'restart', reason: 'scheduled_restart', attempt: this.restartCount });
      this.logger.info('Restarting playback after fatal error...');
      this.setupHls();
      const url = this.lastSource.master || this.lastSource.source || '';
      if (url && this.hls && this.video) {
        this.hls.loadSource(url);
        this.hls.attachMedia(this.video);
      }
    }, delay);
  }

  private clearRestartTimeout(): void {
    if (this.restartTimeout) {
      clearTimeout(this.restartTimeout);
      this.restartTimeout = null;
    }
  }

  // --- Error utilities (delegates to errorUtils module) ---

  private extractResponseCode(data: unknown): number | null {
    return _extractResponseCode(data);
  }

  private isAccessDeniedError(data: unknown, responseCode: number | null): boolean {
    return _isAccessDeniedError(data, responseCode);
  }

  private isPlayAbortError(error: unknown): boolean {
    return _isPlayAbortError(error);
  }

  private getErrorMessage(error: unknown): string {
    return _getErrorMessage(error);
  }

  // --- Speed monitor ---

  private startSpeedMonitor(): void {
    this.speedReportTimer = startSpeedMonitorTimer(
      this.speedReportTimer,
      SPEED_REPORT_INTERVAL,
      this.getSpeedReportSnapshot.bind(this),
      this.emitSpeedReport.bind(this),
    );
  }

  private stopSpeedMonitor(): void {
    this.speedReportTimer = stopSpeedMonitorTimer(this.speedReportTimer);
  }

  private getAverageSpeed(): number {
    return calcAverageSpeed(this.speedDownloadSpeeds);
  }

  private getOverallSpeed(): number {
    return calcOverallSpeed(this.speedTotalBytes, this.speedTotalTime);
  }

  private getSpeedReportSnapshot(): SpeedReportSnapshot {
    return {
      downloadSpeeds: this.speedDownloadSpeeds,
      totalBytes: this.speedTotalBytes,
      totalTimeMs: this.speedTotalTime,
      src: this.lastSource?.master ?? this.lastSource?.source,
      quality: this.store.getState().quality,
    };
  }

  private emitSpeedReport(payload: SpeedReportPayload): void {
    this.eventBus.emit('hls:speed:report', payload);
  }

}

